/**
 * CaseRepository — Carga de casos clínicos desde JSON externos.
 *
 * Los 3 casos base (iam, sepsis, epoc) siguen generados por PatientLoader
 * (lógica en código). Esta clase habilita CASOS EXTRA:
 *   - JSON estáticos en /public/data/cases/custom/*.json
 *   - URLs arbitrarias (ej. endpoint del backend en el futuro)
 *
 * El formato es el mismo PatientGraph que produce PatientLoader: así cualquier
 * caso serializado vale. La validación es mínima (los errores de schema se
 * manifiestan al cargar en Patient, no acá).
 */

import type { PatientGraph } from '../types/medical';
import { authService } from '../auth/AuthService';

export interface CustomCaseSummary {
  id: number;
  slug: string;
  display_label: string;
  chief_complaint: string | null;
  triage_priority: string;
  author_id: number;
  author_name: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomCaseFull extends CustomCaseSummary {
  payload: PatientGraph;
}

export class CaseRepository {
  private cache = new Map<string, PatientGraph>();

  /**
   * Carga un caso desde URL JSON. Resultados se cachean por URL para que
   * recargar el mismo caso no vuelva a golpear la red.
   */
  async loadFromUrl(url: string): Promise<PatientGraph> {
    const cached = this.cache.get(url);
    if (cached) return this.clone(cached);

    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`No se pudo cargar caso desde ${url}: HTTP ${res.status}`);
    }
    const data = (await res.json()) as PatientGraph;
    this.validate(data);
    this.cache.set(url, data);
    return this.clone(data);
  }

  /**
   * Carga un caso desde /data/cases/custom/<slug>.json (servido por Vite).
   */
  async loadCustom(slug: string): Promise<PatientGraph> {
    return this.loadFromUrl(`/data/cases/custom/${slug}.json`);
  }

  /**
   * Valida y devuelve un PatientGraph desde un objeto ya parseado.
   * Útil para admin UI donde el profesor pega el JSON manualmente.
   */
  loadFromObject(data: unknown): PatientGraph {
    const graph = data as PatientGraph;
    this.validate(graph);
    return this.clone(graph);
  }

  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Expone la validación para que el admin UI pueda mostrar errores antes
   * de cargar. Devuelve null si OK, string con el motivo si falla.
   */
  validateLax(data: unknown): string | null {
    try {
      this.validate(data as PatientGraph);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Backend (casos persistidos por el profesor)
  // ═══════════════════════════════════════════════════════════════════════

  async listRemote(): Promise<CustomCaseSummary[]> {
    if (!authService.isAuthenticated) return [];
    try {
      return await authService.request<CustomCaseSummary[]>('/cases/custom');
    } catch (e) {
      console.warn('[CaseRepository] backend no disponible:', e);
      return [];
    }
  }

  async getRemote(slug: string): Promise<PatientGraph> {
    const full = await authService.request<CustomCaseFull>(`/cases/custom/${slug}`);
    this.validate(full.payload);
    return this.clone(full.payload);
  }

  async createRemote(
    slug: string,
    displayLabel: string,
    payload: PatientGraph,
    isPublic = true
  ): Promise<CustomCaseSummary> {
    this.validate(payload);
    return authService.request<CustomCaseSummary>('/cases/custom', {
      method: 'POST',
      body: JSON.stringify({
        slug,
        display_label: displayLabel,
        payload,
        is_public: isPublic,
      }),
    });
  }

  async deleteRemote(slug: string): Promise<void> {
    await authService.request<void>(`/cases/custom/${slug}`, { method: 'DELETE' });
  }

  private validate(data: PatientGraph): void {
    if (!data.id || typeof data.id !== 'string') {
      throw new Error("JSON inválido: falta 'id' (string)");
    }
    if (!Array.isArray(data.nodes)) {
      throw new Error("JSON inválido: falta 'nodes' (array)");
    }
    if (!data.demographics) {
      throw new Error("JSON inválido: falta 'demographics'");
    }
    if (!data.triagePriority) {
      throw new Error("JSON inválido: falta 'triagePriority'");
    }
  }

  /**
   * Clone superficial para evitar que mutaciones en runtime (ej. revealNode
   * cambiando clinicalStatus) contaminen la copia cacheada.
   */
  private clone(data: PatientGraph): PatientGraph {
    return structuredClone(data);
  }
}

export const caseRepository = new CaseRepository();
